import { css } from '@emotion/react';
import { useMemo, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import SPLoader from '../../components/SPLoader';
import { login } from '../../services/auth';
import firebase from 'firebase/app';
import ForgotScreen from './ForgotScreen';
import { trans } from '../../services/i18n';
import { emailRegex } from '../../reusable/schemas/regex';

export default function LoginScreen() {
    const [state, setState] = useState<{ executing: boolean; email: string; password: string; error?: string }>({
        executing: false,
        email: '',
        password: '',
    });

    const isStyreportalen = useMemo(() => {
        return window.location.hostname === process.env.sentral_hostname;
    }, []);

    const portalUrl = useMemo(() => {
        return isStyreportalen ? process.env.portal_url : process.env.tutti_portal_url;
    }, [isStyreportalen]);

    const [forgotPass, setForgotPass] = useState<boolean>(false);

    const executeLogin = async () => {
        if (state.executing) {
            return;
        }

        setState({ ...state, executing: true });
        try {
            await login(state.email, state.password);
        } catch (e) {
            setState({ ...state, error: (e as firebase.FirebaseError).code, executing: false });
        }
    };

    if (forgotPass) {
        return <ForgotScreen setForgotPass={setForgotPass} />;
    }

    return (
        <div
            css={css`
                display: flex;
                justify-content: center;
                margin-top: 1rem;
            `}>
            <Card
                css={css`
                    max-width: 60ch;
                    width: 100%;
                `}>
                <Card.Header className="logoBox">
                    <img
                        src={`${portalUrl}${isStyreportalen ? 'img/styreportalen.png' : 'img/tutti.png'}`}
                        className="img-fluid px-4 py-3"
                        alt={`${isStyreportalen ? 'Styreportalen' : 'Tutti'} logo`}
                    />
                </Card.Header>
                <Card.Body>
                    {state.executing && (
                        <div className="d-flex justify-content-center">
                            <SPLoader />
                        </div>
                    )}

                    {state.error && <Alert variant="danger">{trans('firebase_error:' + state.error)}</Alert>}

                    <form
                        className="d-flex flex-column gap-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                            executeLogin();
                        }}>
                        <Form.Group>
                            <Form.Label>{trans('login:email_address')}</Form.Label>
                            <Form.Control
                                autoComplete="email"
                                placeholder={trans('login:email_address')}
                                disabled={state.executing}
                                type="text"
                                pattern={/(tokenlogin)|([a-zA-Z0-9.\-+_]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/.source}
                                value={state.email}
                                onChange={({ target: { value } }) => setState({ ...state, email: value })}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>{trans('login:password')}</Form.Label>
                            <Form.Control
                                autoComplete="current-password"
                                placeholder={trans('login:password')}
                                disabled={state.executing}
                                type="password"
                                value={state.password}
                                onChange={({ target: { value } }) => setState({ ...state, password: value })}
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-between">
                            <Button type="button" variant="link" onClick={() => setForgotPass(true)}>
                                {trans('login:forgot_password')}
                            </Button>
                            <Button type="submit" disabled={state.executing}>
                                {trans('login:sign_in')}
                            </Button>
                        </div>
                    </form>
                </Card.Body>
            </Card>
        </div>
    );
}
